import * as React from 'react'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { DialogActions, DialogBody, DialogTitle, DialogTrigger, makeStyles, mergeClasses } from '@fluentui/react-components'
import useAxios from '../../../../hooks/useAxios'
import { useTranslation } from 'react-i18next'
import { HammockFormDialogProps } from '../HammockFormDialog'
import { DisbursementMutationDto, DisbursementOverviewDto } from '../../../../config/registrationConfig'
import { BaseMutableOverviewDialogProps } from '../../../../pages/BaseMutableOverview/BaseMutableOverview'
import { InvoiceStatus } from '../../../../config/finance/invoiceConfig'
import { FormikConfig, useFormikContext } from 'formik'
import {
    bundleIcon,
    DeleteRegular,
    DismissFilled,
    DismissRegular,
    SaveEditFilled,
    SaveEditRegular,
    SaveFilled,
    SaveRegular,
} from '@fluentui/react-icons'
import { useApplicationStyles } from '../../../../pages/App'
import DisbursementMutationDialogBodyContent from './DisbursementMutationDialogBodyContent'
import TooltippedButton from '../../Button/TooltippedButton'
import { useCounter } from 'usehooks-ts'

const CloseIcon = bundleIcon(DismissFilled, DismissRegular)
const SaveIcon = bundleIcon(SaveFilled, SaveRegular)
const SaveAndNewIcon = bundleIcon(SaveEditFilled, SaveEditRegular)

const useStyles = makeStyles({
    actions: {
        width: '100%',
    },
})

export default function DisbursementMutationDialogBody(
    props: HammockFormDialogProps<DisbursementMutationDto, BaseMutableOverviewDialogProps<DisbursementOverviewDto>>,
): ReactElement {
    const axios = useAxios()

    const appStyles = useApplicationStyles()
    const styles = useStyles()

    const { t: translate } = useTranslation()

    const [submitVariant, setSubmitVariant] = useState<'save' | 'saveAndNew'>('save')
    const { count: focusTrigger, increment: updateFocusTrigger } = useCounter(0)
    const { initialValues, submitForm, isSubmitting, resetForm } = useFormikContext<DisbursementMutationDto>()

    const dialogBodyProps = useMemo(() => props.dialogFormBodyProps?.() ?? ({} as BaseMutableOverviewDialogProps<DisbursementOverviewDto>), [props])

    const handleSubmit = useCallback<FormikConfig<DisbursementMutationDto>['onSubmit']>(
        values =>
            dialogBodyProps.id
                ? axios.put(`/registration/disbursement/${dialogBodyProps.id}`, values)
                : axios
                      .post(`/registration/disbursement`, values)
                      .then(() => {
                          if (submitVariant === 'saveAndNew') {
                              resetForm({
                                  values: {
                                      ...initialValues,
                                      currency: values.currency,
                                      date: values.date,
                                      fileId: values.fileId,
                                      userId: values.userId,
                                  },
                              })
                              updateFocusTrigger()
                          } else {
                              resetForm()
                          }
                      })
                      .then(() => submitVariant === 'saveAndNew'),
        [submitVariant],
    )

    useEffect(() => {
        props.setOnSubmit?.(handleSubmit)
    }, [handleSubmit])

    const handleDelete = () =>
        axios
            .delete(`/registration/disbursement/${dialogBodyProps.id}`)
            .then(() => resetForm())
            .then(() => props.onDismiss())
    const handleCancel = () => {
        resetForm()
        props.onDismiss()
    }

    useEffect(() => updateFocusTrigger(), [])

    const isInvoiceFinal: boolean =
        dialogBodyProps.item?.invoice?.status !== undefined && dialogBodyProps.item?.invoice?.status !== InvoiceStatus.Draft

    return (
        <DialogBody>
            <DialogTitle>
                {dialogBodyProps.id ? translate('disbursement.edit.dialog.edit') : translate('disbursement.edit.dialog.create')}
            </DialogTitle>
            <DisbursementMutationDialogBodyContent {...props} focusTrigger={focusTrigger} updateFocusTrigger={updateFocusTrigger} />
            <DialogActions>
                <div className={mergeClasses(styles.actions, appStyles.spaceBetween)}>
                    {dialogBodyProps.id && (
                        <TooltippedButton
                            appearance="subtle"
                            display="iconOnly"
                            label={translate('action.delete')}
                            icon={<DeleteRegular />}
                            onClick={() => handleDelete()}
                            disabled={isSubmitting || dialogBodyProps.item?.invoice?.status !== undefined}
                        />
                    )}
                    {!dialogBodyProps.id && (
                        <TooltippedButton
                            appearance="subtle"
                            label={translate('action.saveandnew')}
                            icon={<SaveAndNewIcon />}
                            onClick={() => {
                                setSubmitVariant('saveAndNew')
                                submitForm()
                            }}
                            disabled={isSubmitting || isInvoiceFinal}
                        />
                    )}
                    {!dialogBodyProps.id && <div></div>}
                    <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS)}>
                        <TooltippedButton
                            appearance="primary"
                            label={translate('action.save')}
                            icon={<SaveIcon />}
                            onClick={() => {
                                setSubmitVariant('save')
                                submitForm()
                            }}
                            disabled={isSubmitting || isInvoiceFinal}
                        />
                        <DialogTrigger disableButtonEnhancement>
                            <TooltippedButton
                                appearance="secondary"
                                label={translate('action.cancel')}
                                icon={<CloseIcon />}
                                onClick={handleCancel}
                                disabled={isSubmitting}
                            />
                        </DialogTrigger>
                    </div>
                </div>
            </DialogActions>
        </DialogBody>
    )
}
